import { SalesforceProduct } from '~/lib/salesforce/types';

/**
 * Only the three fields the matching actually reads. Derived from `SalesforceProduct` so the
 * field types stay authoritative, but narrow enough that an Algolia-shaped product satisfies it
 * without a cast.
 */
export type ProductImageSource = Pick<SalesforceProduct, 'id' | 'imageGroups' | 'variationValues'>;

export interface ImageOptions {
    viewType?: 'large' | 'swatch';
    attributesToMatch?: Record<string, unknown>;
    limit?: number;
}

/**
 * The image groups whose variation attributes all match the product, newest-style: matching is
 * driven by the product's own `variationValues` unless `attributesToMatch` overrides them.
 */
export const getSalesforceProductImageGroups = (
    product: ProductImageSource,
    options?: ImageOptions,
) => {
    const { viewType = 'large', attributesToMatch, limit = -1 } = options || {};

    const imageGroups = product.imageGroups?.reduce(
        (collection, group) => {
            // If a limit is specified, we will no longer collect image groups to save time.
            if (limit > 0 && collection?.length === limit) return collection;

            // We will skip groups that don't match our selected view type.
            if (group.viewType !== viewType) return collection;

            const isFullMatch = group.variationAttributes?.every((attribute) => {
                // We will use "attributesToMatch" if provided. Else, we will use the products default variation attributes.
                const attributesToCompareTo = attributesToMatch || product.variationValues;
                const attributeValue: unknown = attributesToCompareTo?.[attribute.id];

                // If there's value to compare to, we can consider it a match.
                if (!attributeValue) return true;

                // Our product attribute's value should match at least one of the group's attribute's value.
                return attribute.values?.some((entry) => {
                    /**
                     * For some reason, the old implementation also compares "size" to the "product.id".
                     * I don't get why but I kept it for historical purposes and to match the old implementation as is.
                     */
                    if (attribute.id === 'size') {
                        return attributeValue === entry.value || product.id === entry.value;
                    }

                    return attributeValue === entry.value;
                });
            });

            // If we find a full match, we will include that image group in our images.
            if (isFullMatch) return collection?.concat(group);

            return collection;
        },
        [] as ProductImageSource['imageGroups'],
    );

    return imageGroups;
};

export const getSalesforceProductImages = (product: ProductImageSource, options?: ImageOptions) => {
    const imageGroups = getSalesforceProductImageGroups(product, options);

    return imageGroups?.flatMap((group) => group.images);
};
